/**
 * ProviderOperationsPage — list operations assigned to the provider.
 * Backend scoping ensures only own ops are returned. Clicking opens the same
 * OperationDetailDrawer used by admin (it's RBAC-aware on the backend).
 */
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useRealtime } from '@/contexts/RealtimeContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, RefreshCw, Archive, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { listOperations, fetchStateMachines } from '@/services/workflowApi';
import { fetchWithFallback } from '@/services/resilientCache';
import StateBadge from '@/components/workflow/StateBadge';
import OperationDetailDrawer from '@/components/workflow/OperationDetailDrawer';
import { opTypeLabelEs } from '@/components/workflow/labels';

const ProviderOperationsPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const { subscribe } = useRealtime();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [ops, setOps] = useState([]);
  const [stateMachines, setStateMachines] = useState(null);
  const [drawerId, setDrawerId] = useState(params.get('op'));
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [staleAge, setStaleAge] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    const uid = user?.id;
    const [r, sm] = await Promise.all([
      fetchWithFallback('provider:operations', uid, () => listOperations(getAuthHeaders, { limit: 200 })),
      fetchWithFallback('provider:state-machines', uid, () => fetchStateMachines(getAuthHeaders)),
    ]);
    setOps(r.data?.operations || []);
    setStateMachines(sm.data);
    const ages = [r, sm].filter((x) => x.fromCache).map((x) => x.age);
    setStaleAge(ages.length ? Math.max(...ages) : 0);
    setLoading(false);
  }, [getAuthHeaders, user?.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const offs = [
      subscribe('operation.assigned', load),
      subscribe('operation.transition', load),
    ];
    return () => offs.forEach((f) => f && f());
  }, [subscribe, load]);

  // Auto-refetch when network returns
  useEffect(() => {
    const onOnline = () => load();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [load]);

  const filt = ops.filter((o) => !search ||
    [o.id, o.route_origin, o.route_destination, o.passenger_names?.join(' ')].filter(Boolean).some((v) => String(v).toLowerCase().includes(search.toLowerCase()))
  );

  const openDrawer = (id) => { setDrawerId(id); setParams({ op: id }); };
  const closeDrawer = () => { setDrawerId(null); setParams({}); };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Mis Operaciones</h1>
          <p className="text-xs text-slate-500">{ops.length} asignadas a ti</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar" className="pl-7 h-8 w-56 text-xs" data-testid="provider-ops-search" />
          </div>
          {staleAge > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 ring-1 ring-amber-300 px-2 py-0.5 rounded-full" data-testid="ops-cache-badge">
              <Archive className="w-3 h-3" />cache · {Math.round(staleAge / 1000)}s
            </span>
          )}
          <Button size="sm" variant="outline" onClick={load} data-testid="provider-ops-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1" />Refrescar</Button>
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="p-3 text-left">ID</th>
                <th className="p-3 text-left">Tipo</th>
                <th className="p-3 text-left">Estado</th>
                <th className="p-3 text-left">Ruta</th>
                <th className="p-3 text-left">Pasajeros</th>
                <th className="p-3 text-left">Actualizado</th>
                <th className="p-3 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="7" className="p-6 text-center text-slate-400">Cargando…</td></tr>
              : filt.length === 0 ? <tr><td colSpan="7" className="p-6 text-center text-slate-400 italic">Sin operaciones</td></tr>
              : filt.map((o) => (
                <tr key={o.id} className="border-t hover:bg-slate-50" data-testid={`provider-op-row-${o.id}`}>
                  <td className="p-3 font-mono text-[10px]">{o.id.slice(0, 12)}…</td>
                  <td className="p-3">{opTypeLabelEs(o.type)}</td>
                  <td className="p-3"><StateBadge state={o.state} /></td>
                  <td className="p-3">{o.route_origin && o.route_destination ? `${o.route_origin} → ${o.route_destination}` : '—'}</td>
                  <td className="p-3 truncate max-w-[150px]">{o.passenger_names?.join(', ') || '—'}</td>
                  <td className="p-3 text-slate-500">{new Date(o.updated_at).toLocaleString()}</td>
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="outline" onClick={() => openDrawer(o.id)} data-testid={`provider-op-open-${o.id}`}>Abrir</Button>
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/provider/operations/${o.id}`)} data-testid={`provider-op-detail-${o.id}`} title="Ver detalle">
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {drawerId && (
        <OperationDetailDrawer
          operationId={drawerId}
          stateMachines={stateMachines}
          onClose={closeDrawer}
          onChanged={load}
        />
      )}
    </div>
  );
};

export default ProviderOperationsPage;
